import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchSpot, updateSpot } from '../../store/slices/savedSpotsSlice';
import Header from '../../layout/Header'
import MobileNav from '../../layout/MobileNav'
import PageNavSavedLocation from '../../layout/PageNavSavedLocation';
import ButtonLightBlue from '../../components/buttons/ButtonLightBlue';
import DualSlider from '../../components/fields/DualSlider';
import DirectionPicker from '../../components/fields/DirectionPicker';
import SportTypePicker from '../../components/fields/SportTypePicker'
import {Helmet} from "react-helmet";

const SpotEditPreferences = () => {

    const { id } = useParams();
    const { spots, errors, isUpdating } = useSelector(state => state.savedSpots);
    const currentSpot = id ? spots[id] : null;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        name: currentSpot?.name || '',
        preferred_wave_height_min: Number(currentSpot.wave_height_min) || 0.5,
        preferred_wave_height_max: Number(currentSpot.wave_height_max) || 2,
        preferred_swell_period_min: Number(currentSpot.swell_period_min) || 8,
        preferred_swell_period_max: Number(currentSpot.swell_period_max) || 16,
        preferred_wind_speed_min: Number(currentSpot.wind_speed_min) || 0,
        preferred_wind_speed_max: Number(currentSpot.wind_speed_max) || 25,
        preferred_wind_direction: Number(currentSpot.wind_direction) || 0,
        preferred_swell_direction: Number(currentSpot.swell_direction) || 180,
        preferred_sport: currentSpot.preferred_sport || ''
    });

    React.useEffect(() => {
        dispatch(fetchSpot(id));
    }, [dispatch, id]);

    React.useEffect(() => {
        if (currentSpot) {
            setFormData({
                name: currentSpot.name || '',
                preferred_wave_height_min: Number(currentSpot.preferred_wave_height_min) || 1.1,
                preferred_wave_height_max: Number(currentSpot.preferred_wave_height_max) || 3.4,
                preferred_swell_period_min: Number(currentSpot.preferred_swell_period_min) || 8,
                preferred_swell_period_max: Number(currentSpot.preferred_swell_period_max) || 16,
                preferred_wind_speed_min: Number(currentSpot.preferred_wind_speed_min) || 0,
                preferred_wind_speed_max: Number(currentSpot.preferred_wind_speed_max) || 25,
                preferred_wind_direction: Number(currentSpot.preferred_wind_direction) || 0,
                preferred_swell_direction: Number(currentSpot.preferred_swell_direction) || 180,
                preferred_sport: currentSpot.preferred_sport || ''
            });
        }
    }, [currentSpot]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleOnSubmit = async (e) => {
        e.preventDefault();
        
        const spotData = {
            preferred_wave_height_min: formData.preferred_wave_height_min,
            preferred_wave_height_max: formData.preferred_wave_height_max,
            preferred_swell_period_min: formData.preferred_swell_period_min,
            preferred_swell_period_max: formData.preferred_swell_period_max,
            preferred_wind_speed_min: formData.preferred_wind_speed_min,
            preferred_wind_speed_max: formData.preferred_wind_speed_max,
            preferred_wind_direction: formData.preferred_wind_direction,
            preferred_swell_direction: formData.preferred_swell_direction,
            preferred_sport: formData.preferred_sport,
        };

        try {

            console.log(spotData);

            const result = await dispatch(updateSpot({spotId: id, spotData})).unwrap();

            console.log('Spot updated successfully:', result);
            navigate('/location/' + id + '/view'); 

        } catch (error) {
            console.error('Failed to update spot:', error);
        }
    };

    if(!currentSpot){
      return <p>Fetching Data...</p>;
    }

    return (
        <>
            <Helmet>
                <title>Edit Ideal Conditions | Oreca Pro</title>
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <Header />
            <MobileNav />
            <div className='py-[80px] px-5 max-w-[1100px] mx-auto'>
                <h1 className='text-2xl font-semibold mb-2 md:mt-8'>{currentSpot.name ? currentSpot.name : "Current Spot"}</h1>
                <p className='text-xs text-custom-light-grey'>
                    {Number(currentSpot.latitude).toFixed(2)}, {Number(currentSpot.longitude).toFixed(2)}
                </p>

                <PageNavSavedLocation id={currentSpot.id} />

                <form onSubmit={handleOnSubmit} className='max-w-[600px] mx-auto mt-10'>
                    {errors && (
                        <div style={{ color: 'red', marginBottom: '10px' }}>
                            {Object.values(errors).map((error, index) => (
                                <div key={index}>{error}</div>
                            ))}
                        </div>
                    )}

                    <div className="mb-8">
                        <h2 className="text-xl font-semibold mb-6 text-white">Ideal Conditions</h2>
                        <p className='mb-8'>
                            Set your ideal conditions for this spot. Forecast ratings will reflect how well conditions align with your preferences.
                        </p>
                        
                        <DualSlider
                            min={0}
                            max={15}
                            step={0.1}
                            minValue={formData.preferred_wave_height_min}
                            maxValue={formData.preferred_wave_height_max}
                            onMinChange={(value) => handleInputChange({ target: { name: 'preferred_wave_height_min', value } })}
                            onMaxChange={(value) => handleInputChange({ target: { name: 'preferred_wave_height_max', value } })}
                            label="Ideal Wave Height"
                            unit="m"
                        />

                        <DualSlider
                            min={0}
                            max={80}
                            step={1}
                            minValue={formData.preferred_wind_speed_min}
                            maxValue={formData.preferred_wind_speed_max}
                            onMinChange={(value) => handleInputChange({ target: { name: 'preferred_wind_speed_min', value } })}
                            onMaxChange={(value) => handleInputChange({ target: { name: 'preferred_wind_speed_max', value } })}
                            label="Ideal Wind Speed"
                            unit="km/h"
                        />

                        <DualSlider
                            min={0}
                            max={35}
                            step={1}
                            minValue={formData.preferred_swell_period_min}
                            maxValue={formData.preferred_swell_period_max}
                            onMinChange={(value) => handleInputChange({ target: { name: 'preferred_swell_period_min', value } })}
                            onMaxChange={(value) => handleInputChange({ target: { name: 'preferred_swell_period_max', value } })}
                            label="Ideal Swell Period"
                            unit="s"
                        />

                        <DirectionPicker
                            value={formData.preferred_wind_direction}
                            onChange={(value) => handleInputChange({ target: { name: 'preferred_wind_direction', value } })}
                            label="Ideal Wind Direction"
                        />

                        <DirectionPicker
                            value={formData.preferred_swell_direction}
                            onChange={(value) => handleInputChange({ target: { name: 'preferred_swell_direction', value } })}
                            label="Ideal Swell Direction"
                        />

                        <SportTypePicker
                            value={formData.preferred_sport}
                            onChange={(value) => handleInputChange({ target: { name: 'preferred_sport', value } })}
                            label="What sport is this spot's ideal conditions set for?"
                        />

                    </div>

                    <div className='flex flex-col gap-5'>
                        <ButtonLightBlue 
                            type="submit" 
                            text={isUpdating ? 'Saving...' : 'Save Preferences'} 
                        />
                    </div>
                </form>
            </div>
            
        </>
    )
};

export default SpotEditPreferences;
